import { SceneManager } from '../game/SceneManager.js';
import { TrackBuilder } from '../game/TrackBuilder.js';
import { PlayerCharacter } from '../game/PlayerCharacter.js';
import { RunnerController } from '../game/RunnerController.js';
import { ObstacleManager } from '../game/ObstacleManager.js';
import { CollisionSystem } from '../game/CollisionSystem.js';
import { ScoreManager } from '../game/ScoreManager.js';
import { GameLoop } from '../game/GameLoop.js';
import { GameStateMachine, GameState } from './GameStateMachine.js';
import { StartNgClient } from '../platform/StartNgClient.js';
import { DEFAULT_STYLE } from '../platform/platformTypes.js';
import { CONFIG } from './config.js';

import { MenuView } from '../ui/MenuView.js';
import { HudView } from '../ui/HudView.js';
import { ResultsView } from '../ui/ResultsView.js';
import { LeaderboardView } from '../ui/LeaderboardView.js';
import { HowToPlayView } from '../ui/HowToPlayView.js';
import { StatusView } from '../ui/StatusView.js';
import { StyleSimulator } from '../ui/StyleSimulator.js';

export class GameApp {
  constructor() {
    this.container = document.getElementById('canvas-container');
    this.uiRoot = document.getElementById('ui-root');
    this.fallbackEl = document.getElementById('webgl-fallback');

    this.stateMachine = new GameStateMachine(GameState.BOOTING);
    this.startNgClient = new StartNgClient();
    this.sceneManager = null;
    this.trackBuilder = null;
    this.character = null;
    this.runnerController = null;
    this.obstacleManager = null;
    this.collisionSystem = null;
    this.scoreManager = new ScoreManager();
    this.gameLoop = null;

    this.worldSpeed = CONFIG.WORLD.INITIAL_SPEED;
    this.playerInfo = null;
    this.runnerPos = { x: 0, y: 1.0, z: CONFIG.RUNNER.FIXED_Z };

    // UI Views
    this.menuView = null;
    this.hudView = null;
    this.resultsView = null;
    this.leaderboardView = null;
    this.howToPlayView = null;
    this.statusView = null;
    this.styleSimulator = null;

    this.onFocusSync = this.onFocusSync.bind(this);
  }

  async init() {
    // 1. Initialize Start.ng connect client
    this.startNgClient.init();

    // 2. Initialize Three.js Scene
    this.sceneManager = new SceneManager(this.container);
    const initialized = this.sceneManager.init();

    if (!initialized) {
      if (this.fallbackEl) {
        this.fallbackEl.style.display = 'flex';
      }
      this.stateMachine.transitionTo(GameState.ERROR_RECOVERY, { reason: 'webgl_unsupported' });
      return false;
    }

    // 3. Build Track, Player Character & Scene Systems
    this.trackBuilder = new TrackBuilder(this.sceneManager.scene);
    this.trackBuilder.build();

    this.character = new PlayerCharacter(this.sceneManager.scene);
    this.character.create(DEFAULT_STYLE);

    this.runnerController = new RunnerController(this.character, {
      onPauseToggle: () => this.togglePause(),
    });
    this.runnerController.attachListeners(this.container);

    this.obstacleManager = new ObstacleManager(this.sceneManager.scene);

    this.collisionSystem = new CollisionSystem({
      onCollision: (obstacle) => this.handleCollision(obstacle),
      onOrbCollected: (orb) => this.handleOrbCollected(orb),
    });

    // 4. Initialize UI Overlays
    this.initUI();

    // 5. Connect State Machine Listeners
    this.stateMachine.onStateChange((nextState, prevState, payload) => {
      this.handleStateTransition(nextState, prevState, payload);
    });

    // 6. Initialize Single Game Loop
    this.gameLoop = new GameLoop({
      onUpdate: (dt) => this.update(dt),
      onRender: () => this.render(),
    });
    this.gameLoop.start();

    // 7. Tab Synchronization
    window.addEventListener('focus', this.onFocusSync, false);

    // 8. Boot sequence: fetch player identity
    this.stateMachine.transitionTo(GameState.IDENTITY_LOADING);
    await this.loadIdentity();

    return true;
  }

  initUI() {
    // Menu View
    this.menuView = new MenuView(this.uiRoot, {
      onPlay: () => this.startRun(),
      onHowToPlay: () => this.howToPlayView.show(),
      onLeaderboard: () => this.openLeaderboard(),
      onSignIn: () => {
        window.location.href = this.startNgClient.getSignInUrl();
      },
    });

    // HUD View
    this.hudView = new HudView(this.uiRoot, {
      onPause: () => this.togglePause(),
      onMoveLeft: () => this.runnerController.moveLeft(),
      onMoveRight: () => this.runnerController.moveRight(),
      onJump: () => this.runnerController.jump(),
    });

    // Results View
    this.resultsView = new ResultsView(this.uiRoot, {
      onPlayAgain: () => this.startRun(),
      onMainMenu: () => this.stateMachine.transitionTo(GameState.MENU),
      onLeaderboard: () => this.openLeaderboard(),
      onRetrySave: () => this.submitFinalScore(),
      onSignIn: () => {
        window.location.href = this.startNgClient.getSignInUrl();
      },
    });

    // Leaderboard View
    this.leaderboardView = new LeaderboardView(this.uiRoot, {
      onBack: () => this.stateMachine.transitionTo(GameState.MENU),
      onRefresh: () => this.fetchLeaderboardData(),
    });

    // How to Play View
    this.howToPlayView = new HowToPlayView(this.uiRoot, {
      onClose: () => this.howToPlayView.hide(),
    });

    // Status View
    this.statusView = new StatusView(this.uiRoot, {
      onResume: () => this.togglePause(),
      onRestart: () => {
        this.statusView.hidePause();
        this.startRun();
      },
      onMainMenu: () => {
        this.statusView.hidePause();
        this.stateMachine.transitionTo(GameState.MENU);
      },
    });

    // Live Style Simulator (Dev-Only)
    this.styleSimulator = new StyleSimulator(this.character, {
      onStyleChange: (newStyle) => {
        if (this.playerInfo?.player) {
          this.playerInfo.player.style = newStyle;
        }
      },
    });
  }

  async loadIdentity() {
    try {
      this.playerInfo = await this.startNgClient.getCurrentPlayer();
      if (this.playerInfo?.player?.style) {
        this.character.applyStyle(this.playerInfo.player.style);
      }
      this.menuView.updatePlayer(this.playerInfo);
      this.stateMachine.transitionTo(GameState.MENU);
    } catch {
      this.stateMachine.transitionTo(GameState.MENU);
    }
  }

  async onFocusSync() {
    // Silently re-check identity when user returns to tab
    const refreshed = await this.startNgClient.refreshPlayerIdentity();
    if (refreshed && refreshed.player && refreshed.player.style) {
      this.playerInfo = refreshed;
      this.character.applyStyle(refreshed.player.style);
      this.menuView.updatePlayer(refreshed);
    }
  }

  startRun() {
    this.scoreManager.startNewRun();
    this.worldSpeed = CONFIG.WORLD.INITIAL_SPEED;
    this.obstacleManager.reset();
    this.runnerController.reset();

    this.stateMachine.transitionTo(GameState.PLAYING);
  }

  togglePause() {
    const currentState = this.stateMachine.getState();
    if (currentState === GameState.PLAYING) {
      this.stateMachine.transitionTo(GameState.PAUSED);
    } else if (currentState === GameState.PAUSED) {
      this.stateMachine.transitionTo(GameState.PLAYING);
    }
  }

  handleCollision() {
    const currentState = this.stateMachine.getState();
    if (currentState !== GameState.PLAYING) return;

    this.runnerController.setEnabled(false);
    const summary = this.scoreManager.freeze();

    this.stateMachine.transitionTo(GameState.GAME_OVER, summary);
  }

  handleOrbCollected() {
    this.scoreManager.addOrb();
    if (this.character) {
      this.character.triggerOrbAbsorption();
    }
    this.statusView.showToast('+1 Point!', 'info', 800);
  }

  async submitFinalScore() {
    const summary = this.scoreManager.getSummary();
    this.resultsView.setSubmissionState('submitting');

    if (this.playerInfo?.status === 'guest') {
      this.resultsView.setSubmissionState('guest');
      return;
    }

    const res = await this.startNgClient.submitScore(summary.score, summary.runId);
    if (res.ok) {
      this.scoreManager.markSubmitted();
      this.resultsView.setSubmissionState('saved', res.message);
    } else {
      this.resultsView.setSubmissionState('error', res.message);
    }
  }

  async openLeaderboard() {
    this.stateMachine.transitionTo(GameState.LEADERBOARD);
    await this.fetchLeaderboardData();
  }

  async fetchLeaderboardData() {
    this.leaderboardView.showLoading();
    const res = await this.startNgClient.getLeaderboard(CONFIG.PLATFORM.LEADERBOARD_LIMIT);
    if (res.ok) {
      this.leaderboardView.setEntries(res.entries, this.playerInfo?.player?.name);
    } else {
      this.leaderboardView.showError(res.reason === 'network_error' ? 'Network error connecting to Start.ng.' : 'Leaderboard unavailable.');
    }
  }

  handleStateTransition(nextState) {
    // Hide all views first
    this.menuView.hide();
    this.hudView.hide();
    this.resultsView.hide();
    this.leaderboardView.hide();
    this.statusView.hidePause();

    switch (nextState) {
      case GameState.MENU:
        this.runnerController.setEnabled(false);
        this.menuView.show();
        break;

      case GameState.PLAYING:
        this.runnerController.setEnabled(true);
        this.hudView.show();
        break;

      case GameState.PAUSED:
        this.runnerController.setEnabled(false);
        this.hudView.show();
        this.statusView.showPause();
        break;

      case GameState.GAME_OVER:
        this.runnerController.setEnabled(false);
        this.stateMachine.transitionTo(GameState.SUBMITTING_SCORE);
        break;

      case GameState.SUBMITTING_SCORE:
        this.resultsView.setResults(this.scoreManager.getSummary());
        this.resultsView.show();
        this.submitFinalScore().then(() => {
          this.stateMachine.transitionTo(GameState.RESULTS);
        });
        break;

      case GameState.RESULTS:
        this.resultsView.show();
        break;

      case GameState.LEADERBOARD:
        this.leaderboardView.show();
        break;
    }
  }

  update(dt) {
    const currentState = this.stateMachine.getState();

    if (currentState === GameState.PLAYING) {
      // Speed progression
      if (this.worldSpeed < CONFIG.WORLD.MAX_SPEED) {
        this.worldSpeed += CONFIG.WORLD.ACCELERATION * dt;
      }

      // Update pre-allocated runner center for orb absorption
      this.runnerPos.x = this.character.group.position.x;
      this.runnerPos.y = this.character.group.position.y + 0.9;
      this.runnerPos.z = CONFIG.RUNNER.FIXED_Z;

      // 1. Move world and obstacles toward camera (+Z)
      this.trackBuilder.update(this.worldSpeed, dt);
      this.obstacleManager.update(this.worldSpeed, dt, this.runnerPos);

      // 2. Update runner kinematics & animations
      this.runnerController.update(dt);

      // 3. Update distance and score
      this.scoreManager.updateDistance(this.worldSpeed, dt);

      // 4. Update HUD text (only when changed)
      this.hudView.updateMetrics(
        this.scoreManager.getScore(),
        this.scoreManager.getDistance(),
        this.scoreManager.getOrbs()
      );

      // 5. Collision checks
      this.collisionSystem.checkCollisions(this.character, this.obstacleManager);
    } else if (currentState === GameState.MENU || currentState === GameState.IDENTITY_LOADING) {
      // Idle menu world scroll & breathing
      this.trackBuilder.update(CONFIG.WORLD.INITIAL_SPEED * 0.4, dt);
      this.character.updateAnimation(dt, false);
    }
  }

  render() {
    this.sceneManager.render();
  }

  dispose() {
    window.removeEventListener('focus', this.onFocusSync);
    if (this.runnerController) this.runnerController.dispose();
    if (this.obstacleManager) this.obstacleManager.dispose();
    if (this.trackBuilder) this.trackBuilder.dispose();
    if (this.character) this.character.dispose();
    if (this.sceneManager) this.sceneManager.dispose();
    if (this.startNgClient) this.startNgClient.dispose();
    if (this.gameLoop) this.gameLoop.dispose();
  }
}
